// Single source of truth for this app's cookie/localStorage namespace.
// All auth-related storage is prefixed with this key so the web and coach-ui
// apps can coexist in the same browser without colliding on cookies or
// localStorage entries. Bump this string if you want to invalidate every
// previously-stored value for this app.
const APP_KEY = process.env.REACT_APP_NAME;

// Internal names used by the rest of the app. Kept as plain identifiers so
// the public helper API (getCookie / setCookie / removeCookie / etc.) stays
// unchanged and call sites never need to know about the prefix.
const COOKIE_KEYS = {
  accessToken: `${APP_KEY}_accessToken`,
  refreshToken: `${APP_KEY}_refreshToken`,
};
const STORAGE_KEYS = {
  userInfo: `${APP_KEY}_userInfo`,
};

export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

export function setCookie(name, value, days = 7) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

export function removeCookie(name) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

export function setAuthCookies(accessToken, refreshToken) {
  // Remove existing cookies first
  removeAuthCookies();

  // Set new cookies with secure flag for production
  setCookie(COOKIE_KEYS.accessToken, accessToken, 7);
  setCookie(COOKIE_KEYS.refreshToken, refreshToken, 30); // Refresh token typically has longer expiry
}

export function removeAuthCookies() {
  removeCookie(COOKIE_KEYS.accessToken);
  removeCookie(COOKIE_KEYS.refreshToken);
}

// Backwards-compatible aliases: existing code that calls
// getCookie("accessToken") / getCookie("refreshToken") will transparently
// resolve to the namespaced cookie name below.
export function getAccessToken() {
  return getCookie(COOKIE_KEYS.accessToken);
}

export function getRefreshToken() {
  return getCookie(COOKIE_KEYS.refreshToken);
}

// ---- localStorage helpers ----------------------------------------------------
// Same namespacing strategy as cookies, kept here so all client-side
// persistent state for this app lives in one file.

export function getStorageItem(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

export function setStorageItem(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // Ignore quota / privacy-mode errors; the app continues to function
    // without persistence in that case.
  }
}

export function removeStorageItem(key) {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // Ignore.
  }
}

export function getUserInfo() {
  return getStorageItem(STORAGE_KEYS.userInfo);
}

export function setUserInfo(userInfo) {
  setStorageItem(
    STORAGE_KEYS.userInfo,
    typeof userInfo === "string" ? userInfo : JSON.stringify(userInfo)
  );
}

export function removeUserInfo() {
  removeStorageItem(STORAGE_KEYS.userInfo);
}
